import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import { SVEN_SJANGRE, harSterkMatch, type SvenSjanger } from '@/systems/svenManus';
import { STAT_LABEL } from '@/data/courseCatalog';
import type { AuthorStats } from '@/data/types';
import { animateModalGlide } from '@/ui/modalGlide';

const SHEET_W = 880;

export class SjangerVelgModal {
  private layer?: Phaser.GameObjects.Container;
  private onVelg?: (sjanger: string) => void;

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  open(onVelg: (sjanger: string) => void): void {
    if (this.layer) return;
    this.onVelg = onVelg;
    this.layer = this.scene.add.container(0, 0).setDepth(180);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.78)
      .setOrigin(0, 0)
      .setInteractive();
    backdrop.on('pointerup', () => this.close());
    this.layer.add(backdrop);

    this.render();
    animateModalGlide(this.scene, this.layer);
  }

  close(): void {
    this.layer?.destroy(true);
    this.layer = undefined;
    this.onVelg = undefined;
  }

  private render(): void {
    if (!this.layer) return;
    const sheetX = (GAME_WIDTH - SHEET_W) / 2;
    const sheetY = 80;
    const sheetH = GAME_HEIGHT - 160;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_W, sheetH, COLORS.beige, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.7);
    this.layer.add(sheet);

    const topBorder = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_W, 4, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(topBorder);

    const tittel = this.scene.add
      .text(sheetX + 28, sheetY + 28, 'Velg sjanger', {
        fontFamily: FONTS.display,
        fontSize: fontStr(42),
        color: '#862736',
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);

    const undertittel = this.scene.add
      .text(
        sheetX + 28,
        sheetY + 80,
        'Hvilken bok vil du skrive? Anbefalt-merket markerer god match med dine sterke stats.',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(17),
          color: '#5a4a3a',
          fontStyle: 'italic',
          wordWrap: { width: SHEET_W - 56 },
        },
      )
      .setOrigin(0, 0);
    this.layer.add(undertittel);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_W - 28, sheetY + 18, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(64),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const stats = gameState.spillerForfatter.stats;
    const kortW = (SHEET_W - 28 * 3) / 2;
    const kortH = 200;
    const gap = 24;
    let kortY = sheetY + 190;
    for (let i = 0; i < SVEN_SJANGRE.length; i += 1) {
      const sj = SVEN_SJANGRE[i];
      const kol = i % 2;
      if (kol === 0 && i > 0) {
        kortY += kortH + gap;
      }
      const kortX = sheetX + 28 + kol * (kortW + 28);
      this.byggSjangerKort(kortX, kortY, kortW, kortH, sj, stats);
    }
  }

  private byggSjangerKort(
    x: number,
    y: number,
    w: number,
    h: number,
    sjanger: SvenSjanger,
    stats: AuthorStats,
  ): void {
    if (!this.layer) return;
    const sterk = harSterkMatch(sjanger, stats);

    const bg = this.scene.add
      .rectangle(x, y, w, h, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.ochre, sterk ? 0.95 : 0.55);
    this.layer.add(bg);

    const navn = this.scene.add
      .text(x + 16, y + 14, sjanger.navn, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    this.layer.add(navn);

    if (sterk) {
      const badge = this.scene.add
        .text(0, 0, 'Anbefalt', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(13),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(0, 0);
      const padX = 8;
      const padY = 4;
      const bw = badge.width + padX * 2;
      const bh = badge.height + padY * 2;
      const bx = x + w - 16 - bw;
      const by = y + 16;
      const badgeBg = this.scene.add
        .rectangle(bx, by, bw, bh, COLORS.wine, 1)
        .setOrigin(0, 0);
      badge.setPosition(bx + padX, by + padY);
      this.layer.add([badgeBg, badge]);
    }

    const beskrivelse = this.scene.add
      .text(x + 16, y + 64, sjanger.beskrivelse, {
        fontFamily: FONTS.body,
        fontSize: fontStr(14),
        color: '#5a4a3a',
        wordWrap: { width: w - 32 },
      })
      .setOrigin(0, 0);
    this.layer.add(beskrivelse);

    const sterkeNavn = sjanger.sterkeStats
      .map((k) => `${STAT_LABEL[k]} ${stats[k]}`)
      .join(' · ');
    const sterkeTxt = this.scene.add
      .text(x + 16, y + h - 36, sterkeNavn, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(13),
        color: sterk ? '#862736' : '#7a7060',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    this.layer.add(sterkeTxt);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(COLORS.beige, 1));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.white, 1));
    bg.on('pointerup', () => {
      const cb = this.onVelg;
      this.close();
      cb?.(sjanger.id);
    });
  }
}
